import { sortBy } from "lodash";
import type { ColumnDefinition } from "utils/xlsxImportExport/XlsxImportColumn";
import { ExtractValidateRowType } from "utils/xlsxImportExport/XlsxImportWorkBook";
import type { ValidatedRow, WorkSheetDefinition } from "./XlsxImportWorkSheet";

export default function createDefinitionHelpers<
  TValidatedRow extends ValidatedRow,
>() {
  function workSheet<K extends string>(
    def: WorkSheetDefinition<K, TValidatedRow>,
  ) {
    return def;
  }

  function col<
    K extends keyof TValidatedRow = keyof TValidatedRow,
    V = TValidatedRow[K],
  >(def: ColumnDefinition<TValidatedRow, K, V>) {
    return def;
  }

  return { workSheet, col };
}

export function omitColumns<
  TWorkSheetDefinition extends WorkSheetDefinition,
  TOmitColumnKeys extends keyof ExtractValidateRowType<
    TWorkSheetDefinition["key"],
    TWorkSheetDefinition
  >,
>(
  workSheetDefinition: TWorkSheetDefinition,
  omitColumnKeys: TOmitColumnKeys[],
) {
  const { columnDefinitions, ...def } = workSheetDefinition;
  return {
    ...def,
    columnDefinitions: columnDefinitions.filter(
      (cd: TWorkSheetDefinition["columnDefinitions"][number]) =>
        !omitColumnKeys.includes(cd.key as TOmitColumnKeys),
    ),
  } as unknown as WorkSheetDefinition<
    TWorkSheetDefinition["key"],
    Omit<
      ExtractValidateRowType<TWorkSheetDefinition["key"], TWorkSheetDefinition>,
      TOmitColumnKeys
    >
  >;
}

export function pickColumns<
  TWorkSheetDefinition extends WorkSheetDefinition,
  TPickColumnKeys extends keyof ExtractValidateRowType<
    TWorkSheetDefinition["key"],
    TWorkSheetDefinition
  >,
>(
  workSheetDefinition: TWorkSheetDefinition,
  pickColumnKeys: TPickColumnKeys[],
) {
  const { columnDefinitions, ...def } = workSheetDefinition;
  return {
    ...def,
    columnDefinitions: sortBy(
      columnDefinitions.filter(
        (cd: TWorkSheetDefinition["columnDefinitions"][number]) =>
          pickColumnKeys.includes(cd.key as TPickColumnKeys),
      ),
      [(cd) => pickColumnKeys.indexOf(cd.key as TPickColumnKeys)],
    ),
  } as unknown as WorkSheetDefinition<
    TWorkSheetDefinition["key"],
    Pick<
      ExtractValidateRowType<TWorkSheetDefinition["key"], TWorkSheetDefinition>,
      TPickColumnKeys
    >
  >;
}

export type Overrides<TWorkSheetDef extends WorkSheetDefinition> = {
  header?: string;
  columnDefinitions?: {
    [P in TWorkSheetDef["columnDefinitions"][number]["key"]]?: Omit<
      ColumnDefinition<ExtractValidateRowType<P, TWorkSheetDef>>,
      "key"
    >;
  };
};

export function modifyDefinitions<
  T,
  TWorkSheetDefinition extends WorkSheetDefinition = T extends WorkSheetDefinition
    ? T
    : never,
  TWorkSheetDefinitionForOverride extends WorkSheetDefinition = TWorkSheetDefinition,
>(
  workSheetDefinition: T,
  overrides: Overrides<TWorkSheetDefinitionForOverride>,
) {
  const { columnDefinitions, ...def } =
    workSheetDefinition as unknown as TWorkSheetDefinition;
  const { columnDefinitions: colDefOverrides, ...sheetOverrides } = overrides;
  return {
    ...def,
    ...sheetOverrides,
    columnDefinitions: columnDefinitions.map(
      (cd: TWorkSheetDefinition["columnDefinitions"][number]) =>
        ({
          ...cd,
          ...colDefOverrides?.[
            cd.key as TWorkSheetDefinition["columnDefinitions"][number]["key"]
          ],
        } as TWorkSheetDefinition["columnDefinitions"][number]),
    ),
  } as unknown as T;
}
